/**
 * API URL prefix for the admin payments module.
 * Note: axios.defaults.baseURL already includes the "/api" segment,
 * so we only prepend the admin scope here.
 */
export const PAYMENTS_API_PREFIX = "/admin/payments";

/**
 * Endpoint paths exposed by the admin payments module.
 */
export const PAYMENTS_ENDPOINTS = {
    config: `${PAYMENTS_API_PREFIX}/config`,
    refundsPending: `${PAYMENTS_API_PREFIX}/refunds_pending`,
    refundsHistory: (paymentId) => `${PAYMENTS_API_PREFIX}/refunds_history/${paymentId}`,
    refundsRetry: (paymentId) => `${PAYMENTS_API_PREFIX}/refunds_retry/${paymentId}`,
    markRefunded: (paymentId) => `${PAYMENTS_API_PREFIX}/mark_refunded/${paymentId}`,
    markPaid: (reservationId) => `${PAYMENTS_API_PREFIX}/mark_paid/${reservationId}`,
    sendPaymentLink: `${PAYMENTS_API_PREFIX}/send_payment_link`,
    last: `${PAYMENTS_API_PREFIX}/last`,
    search: (term) => `${PAYMENTS_API_PREFIX}/search/${encodeURIComponent(term)}`,
};

/**
 * Available payment methods supported by the platform.
 */
export const PAYMENT_METHOD_OPTIONS = [
    { value: "card", label: "Carte bancaire" },
    { value: "sepa_credit_transfer", label: "Virement SEPA" },
];

/**
 * Currency codes the platform accepts for payments.
 */
export const PAYMENT_CURRENCY_OPTIONS = ["eur", "usd", "gbp", "chf"];

/**
 * Maximum auto-retry attempts before manual escalation.
 */
export const REFUND_MAX_RETRY = 3;

/**
 * Display labels for ReservationStatus enum returned by the API.
 */
export const RESERVATION_STATUS_LABELS = {
    pending: "En attente",
    accepted: "Acceptée",
    paid: "Payée",
    awaiting_transfer: "En attente du virement",
    declined: "Refusée",
    refund_manual_required: "Remboursement manuel requis",
};

/**
 * Color schemes for ReservationStatus chips.
 */
export const RESERVATION_STATUS_COLORS = {
    paid: { bg: "rgba(76, 175, 80, 0.15)", color: "rgba(129, 199, 132, 1)", border: "rgba(76, 175, 80, 0.4)" },
    accepted: { bg: "rgba(33, 150, 243, 0.15)", color: "rgba(100, 181, 246, 1)", border: "rgba(33, 150, 243, 0.4)" },
    awaiting_transfer: { bg: "rgba(255, 152, 0, 0.15)", color: "rgba(255, 183, 77, 1)", border: "rgba(255, 152, 0, 0.4)" },
    pending: { bg: "rgba(158, 158, 158, 0.15)", color: "rgba(189, 189, 189, 1)", border: "rgba(158, 158, 158, 0.4)" },
    declined: { bg: "rgba(244, 67, 54, 0.15)", color: "rgba(229, 115, 115, 1)", border: "rgba(244, 67, 54, 0.4)" },
    refund_manual_required: { bg: "rgba(244, 67, 54, 0.15)", color: "rgba(229, 115, 115, 1)", border: "rgba(244, 67, 54, 0.4)" },
};

/**
 * Display labels for refund attempt source.
 */
export const REFUND_SOURCE_LABELS = {
    auto: "Automatique",
    retry: "Retry programmé",
    webhook: "Webhook Stripe",
    admin_manual: "Action admin",
};

/**
 * Display labels for refund attempt outcome.
 */
export const REFUND_OUTCOME_LABELS = {
    success: "Succès",
    failed: "Échec",
    escalated: "Escaladé",
};

/**
 * Color schemes for refund outcome chips.
 */
export const REFUND_OUTCOME_COLORS = {
    success: { bg: "rgba(76, 175, 80, 0.15)", color: "rgba(129, 199, 132, 1)", border: "rgba(76, 175, 80, 0.4)" },
    failed: { bg: "rgba(244, 67, 54, 0.15)", color: "rgba(229, 115, 115, 1)", border: "rgba(244, 67, 54, 0.4)" },
    escalated: { bg: "rgba(255, 152, 0, 0.15)", color: "rgba(255, 183, 77, 1)", border: "rgba(255, 152, 0, 0.4)" },
};

/**
 * Default chip color scheme used as fallback.
 */
export const DEFAULT_CHIP_COLOR = {
    bg: "rgba(255, 255, 255, 0.08)",
    color: "rgba(255, 255, 255, 0.7)",
    border: "rgba(255, 255, 255, 0.2)",
};

/**
 * Tab identifiers for the main Payments view.
 */
export const PAYMENTS_TAB_IDS = {
    config: "config",
    refunds: "refunds",
    manual: "manual",
    browse: "browse",
};

/**
 * Display labels for payment providers.
 */
export const PAYMENT_PROVIDER_LABELS = {
    stripe: "Stripe",
    orange_money: "Orange Money",
};

/**
 * Color schemes for payment provider chips.
 */
export const PAYMENT_PROVIDER_COLORS = {
    stripe: { bg: "rgba(99, 102, 241, 0.15)", color: "rgba(165, 180, 252, 1)", border: "rgba(99, 102, 241, 0.4)" },
    orange_money: { bg: "rgba(255, 152, 0, 0.15)", color: "rgba(255, 183, 77, 1)", border: "rgba(255, 152, 0, 0.4)" },
};

/**
 * Detects the payment provider from API fields.
 * Orange Money payments have no payment_intent_id and use a "KantoBiz_" reference.
 * @param {Object} payment - Payment dict from /admin/payments/refunds_pending
 * @returns {"orange_money"|"stripe"}
 */
export function detectPaymentProvider(payment) {
    if (!payment) return "stripe";
    if (payment.reference?.startsWith("KantoBiz_")) return "orange_money";
    if (!payment.payment_intent_id) return "orange_money";
    return "stripe";
}
